import { bangkokDayKey, daysBetween } from './journeyTime'
import { isMessage, isOtherOrder, isVisit } from './journeyDays'

const ORDER_CANCELLED = 'order_cancelled'
const ORDER_RECOVERED = 'order_recovered'
const DEPOSIT_PAID = 'deposit_paid'
const LINE_MESSAGE_RECEIVED = 'line_message_received'
const NEWER_ORDER = 'newer_order'
const CUSTOMER_ORDER_STEPS = ['quotation_viewed', DEPOSIT_PAID]
const NOT_CUSTOMER_EVENTS = ['voucher_received']

const isOrderStep = (step) => step.kind === 'order'
const isNewerOrder = (row) => row.kind === NEWER_ORDER

const afterRows = (after) =>
  [
    ...(after.steps || []),
    ...(after.newer_orders || []).map((order) => ({
      kind: NEWER_ORDER,
      first_at: order.started_at,
      last_at: order.started_at,
      order
    }))
  ].sort((a, b) => Date.parse(a.first_at) - Date.parse(b.first_at))

const sharedProductsOf = (order, orderIds) => {
  const ids = [...new Set((order.item_ids || []).map(String))]
  if (!ids.length || !orderIds.size) return null
  return { shared: ids.filter((id) => orderIds.has(id)).length, total: orderIds.size }
}

const isMostlyTheSame = (shared) => !!shared && shared.shared > 0 && shared.shared * 2 >= shared.total

const similarNewerOrder = (after, orderIds) => {
  for (const order of after?.newer_orders || []) {
    const shared = sharedProductsOf(order, orderIds)
    if (isMostlyTheSame(shared)) return { order, shared }
  }
  return null
}

const isEnded = (after) => after.ends_by === 'paid' || after.ends_by === 'cancelled'

const similarOlderPayment = (after, orderIds) => {
  if (!after || isEnded(after)) return null
  for (const step of (after.steps || []).filter(isOtherOrder)) {
    const shared = sharedProductsOf(step, orderIds)
    if (isMostlyTheSame(shared)) return { step, shared }
  }
  return null
}

const isCustomerStep = (step) =>
  !isMessage(step) &&
  !isOtherOrder(step) &&
  !NOT_CUSTOMER_EVENTS.includes(step.name) &&
  (!isOrderStep(step) || CUSTOMER_ORDER_STEPS.includes(step.name))

const lastCustomerStep = (steps) => {
  const latest = steps
    .filter(isCustomerStep)
    .reduce((found, step) => (!found || Date.parse(step.last_at) >= Date.parse(found.last_at) ? step : found), null)
  if (!latest) return null
  const inner = isVisit(latest) && latest.steps?.length ? latest.steps[latest.steps.length - 1] : latest
  return { name: inner.name, at: latest.last_at }
}

const countOf = (steps, test) => steps.filter(test).reduce((count, step) => count + (step.count || 1), 0)
const messagesSent = (steps) => countOf(steps, isMessage)
const lineMessagesOf = (steps) => countOf(steps, (step) => step.name === LINE_MESSAGE_RECEIVED)
const isOwnStep = (name) => (step) => isOrderStep(step) && step.name === name
const lastRecoveryAt = (steps) =>
  Math.max(-Infinity, ...steps.filter(isOwnStep(ORDER_RECOVERED)).map((step) => Date.parse(step.last_at)))
const depositOf = (steps) => {
  const recoveredAt = lastRecoveryAt(steps)
  return steps.find((step) => isOwnStep(DEPOSIT_PAID)(step) && Date.parse(step.last_at) > recoveredAt)
}
const daysOpen = (startedAt, to) => daysBetween(bangkokDayKey(startedAt), bangkokDayKey(to))

export {
  ORDER_CANCELLED,
  DEPOSIT_PAID,
  isOrderStep,
  isNewerOrder,
  afterRows,
  sharedProductsOf,
  isMostlyTheSame,
  similarNewerOrder,
  isEnded,
  similarOlderPayment,
  lastCustomerStep,
  messagesSent,
  lineMessagesOf,
  depositOf,
  daysOpen
}
